<template>
	<form
		class="bg-white px-6 py-8 xl:px-12 xl:py-10 self-start"
		method="POST"
		@submit="submitForm"
	>
		<fieldset class="mb-8">
			<div class="flex justify-between">
				<label for="shipping-departures" class="label"
					>Where do you want to ship from?</label
				>
				<button
					class="appearance-none bg-transparent rounded-none border-0 underline p-0 mb-3 ml-4 text-sm text-right lg:w-full"
					v-if="form.shippingDeparture.value !== 0"
					@click="resetPorts($event, 'departures')"
				>
					Reset
				</button>
			</div>
			<ValidationProvider
				rules="required|selectDeparture"
				v-slot="{ errors }"
				mode="lazy"
			>
				<div class="form-icon departures relative">
					<select
						id="shipping-departures"
						name="shipping-departures"
						:class="[
							{
								'!border-error':
									(form.shippingDeparture.error &&
										form.shippingDeparture.value == 0) ||
									errors.length,
								'bg-blue-light': form.shippingDeparture.value !== 0,
							},
							'select',
						]"
						@change="getArrivalPorts($event)"
						v-model="form.shippingDeparture.value"
					>
						<option value="0" disabled selected>Select departure port</option>
						<option v-for="item in departures" :value="item" :key="item">
							{{ item }}
						</option>
					</select>
				</div>
				<span
					class="form-error"
					v-if="
						form.shippingDeparture.error &&
						form.shippingDeparture.value == 0 &&
						!errors.length
					"
					>{{ form.shippingDeparture.error }}</span
				>
				<span class="form-error" v-if="errors.length">{{ errors[0] }}</span>
			</ValidationProvider>
		</fieldset>

		<fieldset class="mb-8">
			<div class="flex justify-between">
				<label for="shipping-arrivals" class="label"
					>Where do you want to ship to?</label
				>
				<button
					class="appearance-none bg-transparent rounded-none border-0 underline p-0 mb-3 ml-4 text-sm text-right lg:w-full"
					v-if="form.shippingArrival.value !== 0"
					@click="resetPorts($event, 'arrivals')"
				>
					Reset
				</button>
			</div>

			<ValidationProvider
				rules="required|selectArrival"
				v-slot="{ errors }"
				mode="lazy"
			>
				<div class="form-icon arrivals relative">
					<select
						id="shipping-arrivals"
						name="shipping-arrivals"
						:class="[
							{
								'!border-error':
									(form.shippingArrival.error &&
										form.shippingArrival.value == 0) ||
									errors.length,
								'bg-blue-light': form.shippingArrival.value !== 0,
							},
							'select',
						]"
						@change="getDeparturePorts($event)"
						v-model="form.shippingArrival.value"
					>
						<option value="0" disabled selected>Select arrival port</option>
						<option v-for="item in arrivals" :value="item" :key="item">
							{{ item }}
						</option>
					</select>
				</div>
				<span
					class="form-error"
					v-if="
						form.shippingArrival.error &&
						form.shippingArrival.value == 0 &&
						!errors.length
					"
					>{{ form.shippingArrival.error }}</span
				>
				<span class="form-error" v-if="errors.length">{{ errors[0] }}</span>
			</ValidationProvider>
		</fieldset>

		<fieldset class="mb-8">
			<label for="shipping-date" class="label">Shipment date</label>
			<ValidationProvider rules="required" v-slot="{ errors }" mode="lazy">
				<div class="form-icon calendar relative">
					<DatePicker
						v-model="form.shippingDate.value"
						:min-date="tomorrowDate"
						:locale="{ id: 'en-GB', masks: { L: 'DD/MM/YYYY' } }"
						:popover="{ visibility: 'focus' }"
						@input="onDateChange()"
					>
						<template v-slot="{ inputValue, inputEvents }">
							<input
								id="shipping-date"
								name="shipping-date"
								:class="[
									form.shippingDate.error && form.shippingDate.value == null
										? '!border-error select border border-grey focus:border-error focus:outline-none'
										: 'select border border-grey focus:border-black focus:outline-none rounded-none',
								]"
								:value="inputValue"
								v-on="inputEvents"
								placeholder="Select a date"
							/>
						</template>
					</DatePicker>
				</div>
				<span
					class="form-error"
					v-if="form.shippingDate.error && form.shippingDate.value == null"
					>{{ form.shippingDate.error }}</span
				>
				<span class="form-error" v-if="errors.length">{{ errors[0] }}</span>
			</ValidationProvider>
		</fieldset>
		<fieldset>
			<button class="button w-full justify-between" type="submit">
				<span id="submit-text">Find Schedule</span>
				<span class="w-3 h-3">
					<svg
						class="w-full h-full fill-inherit"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 200 200"
					>
						<polygon
							fill-rule="evenodd"
							clip-rule="evenodd"
							points="121.3,21.3 106.3,36.4 159.2,89.3 0,89.3 0,110.7 159.2,110.7
                            106.3,163.6 121.3,178.7 192.4,107.5 200,100 192.4,92.4 "
						/>
					</svg>
				</span>
			</button>
		</fieldset>
	</form>
</template>

<script>
import axios from "axios";

// Rules
import "../rules.js";

// Components
import { ValidationProvider } from "vee-validate";
import DatePicker from "v-calendar/lib/components/date-picker.umd";

/**
    title: Schedule Form
    data: {
		- departures: This will be an array created by the predator/schedule/ports endpoint.
		- arrivals: This will be an array created by the predator/schedule/ports endpoint.
		- form: {
			- shippingDeparture: An Object consisting of the value & error. The error is populated by validation, and the value is passed to the api endpoint.
			- shippingArrival: An Object consisting of the value & error. The error is populated by validation, and the value is passed to the api endpoint.
			- shippingDate: An Object consisting of the value & error. The error is populated by validation, and the value is passed to the api endpoint.
		}
	}
    components: {
		- ValidationProvider: Used to validate the fields on the front-end
		- DatePicker: Vue Date Picker to create the date field.
	},
	mounted: {
		Once the instance has been mounted, we need to get all ports from the api endpoint and pass them through to the data
	},
    methods: {
		- onDateChange: Due to how the date component renders the Date field, we need to add the form JS manually.
		- submitForm: When the form is submitted we will attempt to send the data to the 'all' endpoint, and catch any errors
	}
**/
export default {
	data: function () {
		return {
			departures: [],
			arrivals: [],
			form: {
				shippingDeparture: {
					value: 0,
					error: null,
				},
				shippingArrival: {
					value: 0,
					error: null,
				},
				shippingDate: {
					value: null,
					error: null,
				},
			},
		};
	},
	components: {
		ValidationProvider,
		DatePicker,
	},
	mounted: function () {
		this.$nextTick(function () {
			axios.get("/actions/predator/schedule/ports").then((response) => {
				this.departures = response.data.departures;
				this.arrivals = response.data.arrivals;
			});
		});
	},
	computed: {
		tomorrowDate: function () {
			const today = new Date();
			const tomorrow = new Date(today);
			return tomorrow.setDate(tomorrow.getDate() + 1);
		},
	},
	methods: {
		resetPorts: function (event, type) {
			event.preventDefault();
			axios.get("/actions/predator/schedule/ports").then((response) => {
				this.departures = response.data.departures;
				this.arrivals = response.data.arrivals;

				switch (type) {
					case "departures":
						this.form.shippingDeparture.value = 0;
						break;
					case "arrivals":
						this.form.shippingArrival.value = 0;
						break;
				}
			});
		},
		getArrivalPorts: function (event) {
			axios
				.get(
					"/actions/predator/schedule/from-port?fromPort=" + event.target.value,
				)
				.then((response) => {
					// Add data here
					const data = response.data;
					if (data) {
						this.arrivals = data.slice(0, data.length);

						if (!this.arrivals.includes(this.form.shippingArrival.value)) {
							this.form.shippingArrival.value = 0;
						}
					}
				})
				.catch((error) => {
					console.log(error);
				});
		},
		getDeparturePorts: function (event) {
			axios
				.get("/actions/predator/schedule/to-port?toPort=" + event.target.value)
				.then((response) => {
					// Add data here
					const data = response.data;
					if (data) {
						this.departures = data.slice(0, data.length);

						if (!this.departures.includes(this.form.shippingDeparture.value)) {
							this.form.shippingDeparture.value = 0;
						}
					}
				})
				.catch((error) => {
					console.log(error);
				});
		},
		onDateChange: function () {
			const shippingDate = document.getElementById("shipping-date");
			shippingDate.classList.add("bg-blue-light");
		},
		submitForm: function (e) {
			// Called when the form is submitted
			e.preventDefault();

			// Create a new formData object
			let formData = new FormData();

			// Add the Craft CSRF Token to the FormData - This is required by the PHP Model
			formData.append("CRAFT_CSRF_TOKEN", window.csrfToken);

			// Lets Format the date
			let shippingDate = this.form.shippingDate.value;
			if (shippingDate) {
				shippingDate = shippingDate.toISOString().split("T")[0];
			}

			formData.append("fromPort", this.form.shippingDeparture.value);
			formData.append("toPort", this.form.shippingArrival.value);
			formData.append("date", shippingDate);

			// Get Button Text
			const buttonText = document.getElementById("submit-text");
			buttonText.innerText = "Finding Schedules";

			// Send the Form Data
			axios
				.post("/actions/predator/schedule/all", formData)
				.then((response) => {
					if (response.data.error) {
						// If an error exists, we want to throw the data to the catch
						throw { response: response.data };
						forms();
					} else {
						// Push the Schedule Results back to the parent
						this.$emit("findResults", response.data);
						buttonText.innerText = "Find Schedule";
					}
				})
				.catch((error) => {
					// Get all errors back from the response
					const errors = error.response;

					// Get the errors related to the form fields
					const formErrors = errors.error;

					// Loop through the form errors and assign them to the correct attributes, doing this will make them display on the front end reactively
					for (const [key, value] of Object.entries(formErrors)) {
						if (this.form[key]) {
							this.form[key].error = value;
						}
					}

					this.$emit("findResults", errors);
					buttonText.innerText = "Find Schedule";
				});
		},
	},
};
</script>
